/**
 * Avatar + StatusBadge + fmt.
 *
 * Avatar — пытается отрендерить картинку из /api/avatars/{userId} если переданы
 * userId + avatarFilename (cache-buster). Если не указано или картинка не
 * загрузилась — fallback на инициалы по name.
 *
 * online: показать зелёную точку «в сети» (низ-право).
 */
import { useState } from "react";

import { userAvatarUrl } from "@/api/dashboard";


interface AvatarProps {
  name: string;
  color?: string;
  size?: number;
  /** UUID пользователя — для /api/avatars/{id} URL. */
  userId?: string | null;
  /** Имя файла аватара — cache-buster при смене (null → инициалы). */
  avatarFilename?: string | null;
  /** Зелёная точка «в сети». */
  online?: boolean;
  /** eager-загрузка картинки (для аватаров ВНУТРИ Virtuoso-ленты: lazy там даёт
   *  мигание «инициалы→фото» при быстром скролле). В модалках/списках — lazy. */
  eager?: boolean;
}

export function Avatar({
  name, color, size = 28, userId, avatarFilename, online, eager,
}: AvatarProps) {
  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const url = userId && avatarFilename ? userAvatarUrl(userId, avatarFilename) : null;
  const [errored, setErrored] = useState(false);
  const showImage = url && !errored;
  const dotSize = Math.max(8, Math.round(size * 0.28));

  return (
    <div
      style={{
        position: "relative",
        width: size, height: size,
        flexShrink: 0,
        display: "inline-block",
      }}
    >
      <div
        style={{
          width: size, height: size, borderRadius: "50%",
          background: color || "#AB2B2B", color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: size * 0.38, fontWeight: 600,
          overflow: "hidden",
        }}
      >
        {showImage ? (
          <img
            src={url}
            alt={name}
            width={size}
            height={size}
            decoding="async"
            loading={eager ? "eager" : "lazy"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={() => setErrored(true)}
          />
        ) : initials}
      </div>
      {online && (
        <span
          className="avatar-online-dot"
          aria-label="в сети"
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: dotSize,
            height: dotSize,
            borderRadius: "50%",
            background: "#3D7A3D",
            border: "2px solid var(--card, #fff)",
            boxShadow: "0 0 0 1px rgba(61,122,61,0.25)",
          }}
        />
      )}
    </div>
  );
}

export type DealStatus = "overdue" | "risk" | "ok" | "new" | string;

export function StatusBadge({ status }: { status: DealStatus }) {
  const map: Record<string, [string, string]> = {
    overdue: ["badge badge-red", "Просрочено"],
    risk:    ["badge badge-amber", "Риск"],
    ok:      ["badge badge-green", "В норме"],
    new:     ["badge badge-ink", "Новая"],
  };
  const [cls, label] = map[status] || ["badge badge-mute", status];
  return <span className={cls}>{label}</span>;
}

export const fmt = {
  rub: (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₽",
  num: (n: number) => new Intl.NumberFormat("ru-RU").format(n),
  pct: (n: number) => `${n.toFixed(0)}%`,
};
